import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { formatDate } from "../../../core/api";
import { getSupportStats, getSupportTickets } from "../../../core/services/supportService";
import MetricCard from "../../../shared/MetricCard";
import PaginationBar from "../../../shared/PaginationBar";

const SupportList = () => {
  const [tickets, setTickets] = useState<any[]>([]);
  const [stats, setStats] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    total: 0,
  });
  const [filters, setFilters] = useState({ search: "", status: "all", category: "all" });
  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setFilters((current) => ({ ...current, search: searchInput }));
    }, 400);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const loadTickets = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(pagination.limit),
        });
        if (filters.search.trim()) params.set("search", filters.search.trim());
        if (filters.status !== "all") params.set("status", filters.status);
        if (filters.category !== "all") params.set("category", filters.category);

        const [listRes, statsRes] = await Promise.all([
          getSupportTickets(params),
          getSupportStats(),
        ]);

        setTickets(listRes.tickets || []);
        setPagination({
          page: listRes.pagination?.page || page,
          limit: listRes.pagination?.limit || pagination.limit,
          totalPages: listRes.pagination?.totalPages || 1,
          total: listRes.pagination?.total || 0,
        });
        setStats(statsRes.stats || {});
      } catch (error: any) {
        Swal.fire("Error", error.message || "Unable to load support tickets", "error");
      } finally {
        setLoading(false);
      }
    },
    [filters, pagination.limit],
  );

  useEffect(() => {
    void loadTickets(1);
  }, [loadTickets]);

  return (
    <>
      <div className="page-hdr">
        <div className="page-hdr-left">
          <div className="page-eyebrow">Customer Care</div>
          <h1 className="page-title">
            User <em>Support</em>
          </h1>
          <div className="page-sub">Manage customer support tickets and replies.</div>
        </div>
      </div>

      <section className="metrics-grid">
        <MetricCard metric={{ label: "Open", value: stats.open || 0, icon: "🟡", tone: "orange" }} />
        <MetricCard metric={{ label: "Closed", value: stats.closed || 0, icon: "📁", tone: "purple" }} />
      </section>

      <div className="toolbar">
        <div className="toolbar-search">
          <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search subject, message or customer..."
          />
        </div>
        <select
          className="filter-select"
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
          <option value="all">All Status</option>
          <option value="open">Open</option>
          <option value="closed">Closed</option>
        </select>
        <select
          className="filter-select"
          value={filters.category}
          onChange={(e) => setFilters({ ...filters, category: e.target.value })}
        >
          <option value="all">All Categories</option>
          <option value="order">Order</option>
          <option value="payment">Payment</option>
          <option value="product">Product</option>
          <option value="account">Account</option>
          <option value="other">Other</option>
        </select>
      </div>

      <section className="table-card">
        <div className="table-head">
          <h2>Support Requests</h2>
          <p>{stats.active || 0} open · {pagination.total || 0} total</p>
        </div>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Subject</th>
                <th>Customer</th>
                <th>Category</th>
                <th>Status</th>
                <th>Created</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="empty-state">Loading tickets...</td></tr>
              ) : tickets.length === 0 ? (
                <tr><td colSpan={6} className="empty-state">No support requests found.</td></tr>
              ) : (
                tickets.map((ticket) => (
                  <tr key={ticket._id}>
                    <td>
                      <strong>{ticket.subject}</strong>
                      <small className="table-muted">{ticket.message?.slice(0, 60)}</small>
                    </td>
                    <td>
                      <strong>{ticket.user?.name || "Customer"}</strong>
                      <small className="table-muted">{ticket.user?.phone || ticket.user?.email}</small>
                    </td>
                    <td><span className="status-badge muted">{ticket.category}</span></td>
                    <td><span className={`status-badge ${ticket.status === "closed" ? "muted" : "warning"}`}>{ticket.status}</span></td>
                    <td>{formatDate(ticket.createdAt)}</td>
                    <td>
                      <Link to={`/support/${ticket._id}`} className="btn-sm btn-primary">View</Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <PaginationBar
          page={pagination.page}
          limit={pagination.limit}
          total={pagination.total}
          totalPages={pagination.totalPages}
          itemLabel="tickets"
          onPageChange={loadTickets}
          onLimitChange={(limit) =>
            setPagination((current) => ({ ...current, page: 1, limit }))
          }
        />
      </section>
    </>
  );
};

export default SupportList;
